// src/components/ProjectsFinanceDashboard.jsx
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getAllProjectsWithTags } from "../services/azureProjectsService";
import { getTimekeepingData } from "../services/azureTimekeepingService";
import { getInvoicedPeriods } from "../services/azureProjectInvoicedPeriodsService";
import { getProjectAssignments } from "../services/azureProjectAssignmentService";
import { getProducts } from "../services/apiService";
import { getProjectPhases } from "../services/azureProjectPhasesService";
import { getAllTeamMembers } from "../services/azureTeamService";
import { getBudgetEntries, calculateDueDate } from "../services/azureBudgetService";
import { getAllRates } from "../services/azureRatesService";
import {
  calculateHours,
  getHourlyRate,
  calculateAmount,
  isEntryInvoiced,
  formatCHF,
  formatDateCH,
} from "../utils/stundenAuszugUtils";
import ZahlungsplanModal from "./ZahlungsplanModal";

const ProjectsFinanceDashboard = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [timekeepingData, setTimekeepingData] = useState([]);
  const [invoicedPeriods, setInvoicedPeriods] = useState({});
  const [projectAssignments, setProjectAssignments] = useState({});
  const [products, setProducts] = useState([]);
  const [projectPhases, setProjectPhases] = useState({});
  const [teamMembers, setTeamMembers] = useState([]);
  const [budgetEntries, setBudgetEntries] = useState([]);
  const [allProjectRates, setAllProjectRates] = useState({});
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("chf");

  // Zahlungsplan modal state
  const [zahlungsplanProject, setZahlungsplanProject] = useState(null);

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    try {
      setLoading(true);

      const [
        projectsData,
        timekeeping,
        productsData,
        team,
        budgetEntriesData,
        ratesData,
      ] = await Promise.all([
        getAllProjectsWithTags("project"),
        getTimekeepingData(),
        getProducts(),
        getAllTeamMembers(),
        getBudgetEntries(),
        getAllRates(),
      ]);

      // Group rates by project code
      const ratesByProject = {};
      ratesData.forEach((r) => {
        if (!ratesByProject[r.projectCode]) ratesByProject[r.projectCode] = [];
        ratesByProject[r.projectCode].push(r);
      });
      setAllProjectRates(ratesByProject);

      const activeProjects = projectsData.filter(
        (p) => !p.projektende || new Date(p.projektende) >= new Date()
      );

      setProjects(activeProjects);
      setTimekeepingData(timekeeping);
      setProducts(productsData);
      setTeamMembers(team);
      setBudgetEntries(budgetEntriesData);

      const invoicedData = {};
      const assignmentsData = {};
      const phasesData = {};

      await Promise.all(
        activeProjects.map(async (project) => {
          const [invoiced, assignments, phases] = await Promise.all([
            getInvoicedPeriods(project.code),
            getProjectAssignments(project.code),
            getProjectPhases(project.code),
          ]);

          invoicedData[project.code] = invoiced;
          assignmentsData[project.code] = assignments;
          phasesData[project.code] = phases;
        })
      );

      setInvoicedPeriods(invoicedData);
      setProjectAssignments(assignmentsData);
      setProjectPhases(phasesData);
    } catch (error) {
      console.error("Error loading projects finance data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Helper: compute financials for a set of entries/budget
  const computeFinancials = (
    projectEntries, invoiced, phases, assignments, projectBudgetEntries, eigenanteilRatio, phase, rates = []
  ) => {
    const phaseEntries = phase
      ? projectEntries.filter((entry) => {
          if (!entry.date) return false;
          if (phase.startDate && entry.date < phase.startDate) return false;
          if (phase.endDate && entry.date > phase.endDate) return false;
          return true;
        })
      : projectEntries;

    const workedEntries = phaseEntries.filter(
      (entry) => !isEntryInvoiced(entry, invoiced)
    );

    let totalWorkedHours = 0;
    let totalWorkedCHF = 0;
    workedEntries.forEach((entry) => {
      const hours = calculateHours(entry);
      const rate = getHourlyRate(entry, phases, assignments, products, teamMembers, rates);
      totalWorkedHours += hours;
      totalWorkedCHF += calculateAmount(entry, rate);
    });

    const billedEntries = phaseEntries.filter((entry) =>
      isEntryInvoiced(entry, invoiced)
    );

    let totalBilledHours = 0;
    let totalBilledCHF = 0;
    billedEntries.forEach((entry) => {
      const hours = calculateHours(entry);
      const rate = getHourlyRate(entry, phases, assignments, products, teamMembers, rates);
      totalBilledHours += hours;
      totalBilledCHF += calculateAmount(entry, rate);
    });

    const phaseBudgetEntries = phase
      ? projectBudgetEntries.filter(
          (e) => e.phase === phase.name || e.phase === phase.id
        )
      : projectBudgetEntries;

    const incomeEntries = phaseBudgetEntries.filter((e) => e.typ === "Einnahme");
    const expenseEntries = phaseBudgetEntries.filter((e) => e.typ === "Ausgabe");

    const incomePaid = incomeEntries
      .filter((e) => e.status === "paid" || e.paid_date)
      .reduce((sum, e) => sum + (e.netto_betrag || e.betrag || 0), 0);

    const expensesPaid = expenseEntries
      .filter((e) => e.status === "paid" || e.paid_date)
      .reduce((sum, e) => sum + (e.netto_betrag || e.betrag || 0), 0);

    const totalBilledCHFFromBudget = incomePaid - expensesPaid;

    const paidIncomes = incomeEntries.filter(
      (e) => e.status === "paid" || e.paid_date
    );
    let lastInvoicedDate = null;
    if (paidIncomes.length > 0) {
      const sortedPaid = [...paidIncomes].sort((a, b) =>
        (b.paid_date || b.wann || "").localeCompare(a.paid_date || a.wann || "")
      );
      lastInvoicedDate = sortedPaid[0].paid_date || sortedPaid[0].wann;
    }

    const today = new Date().toISOString().split("T")[0];
    const getDueDateISO = (e) => {
      const d = calculateDueDate(e);
      return d ? d.toISOString().split("T")[0] : (e.wann || "");
    };
    const futureIncomes = incomeEntries.filter(
      (e) =>
        getDueDateISO(e) >= today &&
        (!e.status || e.status === "expected" || e.status === "expecting")
    );

    let nextIncomeDate = null;
    let nextIncomeAmount = 0;

    if (futureIncomes.length > 0) {
      futureIncomes.sort((a, b) =>
        getDueDateISO(a).localeCompare(getDueDateISO(b))
      );
      const nextIncome = futureIncomes[0];
      nextIncomeDate = getDueDateISO(nextIncome);
      nextIncomeAmount = nextIncome.netto_betrag || nextIncome.betrag || 0;
    }

    const isOverBudget =
      nextIncomeAmount > 0 && totalWorkedCHF > nextIncomeAmount;

    const eigenanteilBilled = totalBilledCHFFromBudget * eigenanteilRatio;
    const nextIncomeNet = nextIncomeAmount * eigenanteilRatio;

    return {
      totalWorkedHours,
      totalWorkedCHF,
      totalBilledHours,
      totalBilledCHF: totalBilledCHFFromBudget,
      lastInvoicedDate,
      nextIncomeDate,
      nextIncomeAmount,
      nextIncomeNet,
      isOverBudget,
      eigenanteilRatio,
      eigenanteilBilled,
    };
  };

  // Calculate project groups with phase sub-rows
  const projectGroups = useMemo(() => {
    const groups = [];
    const today = new Date().toISOString().split("T")[0];

    projects.forEach((project) => {
      const projectCode = project.code;
      const projectEntries = timekeepingData.filter(
        (entry) => entry.code === projectCode
      );
      const invoiced = invoicedPeriods[projectCode] || [];
      const assignments = projectAssignments[projectCode] || [];
      const phases = projectPhases[projectCode] || [];
      const rates = allProjectRates[projectCode] || [];

      const projectBudgetEntries = budgetEntries.filter(
        (entry) => entry.projekt === projectCode
      );

      const totalBudgetIncome = projectBudgetEntries
        .filter((e) => e.typ === "Einnahme")
        .reduce((sum, e) => sum + (e.brutto_betrag || e.betrag || 0), 0);
      const totalBudgetExpense = projectBudgetEntries
        .filter((e) => e.typ === "Ausgabe")
        .reduce((sum, e) => sum + (e.brutto_betrag || e.betrag || 0), 0);
      const eigenanteilRatio = totalBudgetIncome > 0
        ? Math.max(0, Math.min(1, (totalBudgetIncome - totalBudgetExpense) / totalBudgetIncome))
        : 1;

      const activePhases = phases.filter(
        (p) => !p.endDate || p.endDate >= today
      );

      const projectTotal = {
        ...project,
        ...computeFinancials(
          projectEntries, invoiced, phases, assignments, projectBudgetEntries, eigenanteilRatio, null, rates
        ),
        phase: null,
        phaseName: null,
        rowKey: project.id,
        displayCode: project.code,
        displayTitle: project.title,
        allPhases: phases,
        allBudgetEntries: projectBudgetEntries,
        isProjectTotal: true,
        hasPhaseRows: activePhases.length > 1,
      };

      if (activePhases.length > 1) {
        const phaseRows = activePhases.map((phase) => ({
          ...project,
          ...computeFinancials(
            projectEntries, invoiced, phases, assignments, projectBudgetEntries, eigenanteilRatio, phase, rates
          ),
          phase,
          phaseName: phase.name,
          rowKey: `${project.id}_${phase.id}`,
          displayCode: "",
          displayTitle: phase.name,
          allPhases: phases,
          allBudgetEntries: projectBudgetEntries,
          isProjectTotal: false,
          isPhaseRow: true,
        }));

        groups.push({ project: projectTotal, phaseRows });
      } else if (activePhases.length === 1) {
        const singleRow = {
          ...project,
          ...computeFinancials(
            projectEntries, invoiced, phases, assignments, projectBudgetEntries, eigenanteilRatio, activePhases[0], rates
          ),
          phase: activePhases[0],
          phaseName: activePhases[0].name,
          rowKey: project.id,
          displayCode: project.code,
          displayTitle: `${project.title} – ${activePhases[0].name}`,
          allPhases: phases,
          allBudgetEntries: projectBudgetEntries,
          isProjectTotal: true,
          hasPhaseRows: false,
        };
        groups.push({ project: singleRow, phaseRows: [] });
      } else {
        groups.push({ project: projectTotal, phaseRows: [] });
      }
    });

    return groups;
  }, [
    projects,
    timekeepingData,
    invoicedPeriods,
    projectAssignments,
    projectPhases,
    products,
    teamMembers,
    budgetEntries,
  ]);

  // Flatten all rows for bar scaling
  const allRows = useMemo(() => {
    const rows = [];
    projectGroups.forEach((g) => {
      rows.push(g.project);
      g.phaseRows.forEach((r) => rows.push(r));
    });
    return rows;
  }, [projectGroups]);

  // Get max values for bar scaling
  const maxValue = useMemo(() => {
    const maxWorked = Math.max(
      ...allRows.map((p) =>
        viewMode === "chf" ? p.totalWorkedCHF : p.totalWorkedHours
      ),
      1
    );
    const maxBilled = Math.max(
      ...allRows.map((p) =>
        viewMode === "chf" ? Math.abs(p.totalBilledCHF) : p.totalBilledHours
      ),
      1
    );
    return Math.max(maxWorked, maxBilled);
  }, [allRows, viewMode]);

  const handleProjectClick = (row) => {
    navigate(`/main-projects/controlling/${row.code}`);
  };

  const handleStundenauszugClick = (row) => {
    navigate("/main-projects", {
      state: { openStundenAuszug: row.code },
    });
  };

  const handleZahlungsplanClick = (row) => {
    const project = projects.find((p) => p.code === row.code);
    if (project) {
      setZahlungsplanProject({
        project,
        phases: row.allPhases,
        budgetEntries: row.allBudgetEntries,
      });
    }
  };

  const handleZahlungsplanClose = () => {
    setZahlungsplanProject(null);
  };

  const handleBudgetChange = useCallback(() => {
    loadAllData();
  }, []);

  // Render a single row
  const renderRow = (row) => {
    const workedValue =
      viewMode === "chf" ? row.totalWorkedCHF : row.totalWorkedHours;
    const billedValue =
      viewMode === "chf" ? row.totalBilledCHF : row.totalBilledHours;

    const workedPercentage = (workedValue / maxValue) * 100;
    const billedPercentage = (Math.abs(billedValue) / maxValue) * 100;
    const isNegativeBilled = billedValue < 0;

    // Two-color bar segments
    const eigenanteilPercentage = billedPercentage * row.eigenanteilRatio;
    const nonEigenanteilPercentage = billedPercentage - eigenanteilPercentage;

    const isPhase = row.isPhaseRow;

    return (
      <div
        key={row.rowKey}
        className={`project-row ${isPhase ? "phase-sub-row" : ""}`}
      >
        {/* Project name + values */}
        <div className="col-project">
          <div
            className="project-header clickable"
            onClick={() => handleProjectClick(row)}
          >
            {isPhase ? (
              <span className="phase-name">{row.displayTitle}</span>
            ) : (
              <>
                <span className="project-code">{row.displayCode}</span>
                <span className="project-title">{row.displayTitle}</span>
              </>
            )}
          </div>
          <div className="project-values">
            {viewMode === "chf" ? (
              <>
                <div className="values-row">
                  <span className="value-primary">
                    {formatCHF(row.eigenanteilBilled)}
                  </span>
                  <span className="value-separator">/</span>
                  <span className="value-secondary">
                    {formatCHF(row.totalBilledCHF)}
                  </span>
                </div>
                <div className="values-row sub">
                  <span className="value-label">Gearbeitet:</span>
                  <span className="value-secondary">
                    {formatCHF(row.totalWorkedCHF)}
                  </span>
                </div>
              </>
            ) : (
              <>
                <div className="values-row">
                  <span className="value-primary">
                    {row.totalBilledHours.toFixed(1)}h
                  </span>
                </div>
                <div className="values-row sub">
                  <span className="value-label">Gearbeitet:</span>
                  <span className="value-secondary">
                    {row.totalWorkedHours.toFixed(1)}h
                  </span>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Thin bars */}
        <div className="col-bars">
          {/* Gearbeitet bar */}
          <div
            className="thin-bar"
            title={`Gearbeitet: ${viewMode === "chf" ? formatCHF(row.totalWorkedCHF) : row.totalWorkedHours.toFixed(1) + "h"}`}
          >
            <div className="bar-inner" style={{ width: `${workedPercentage}%` }}>
              <div className="bar-segment worked" style={{ width: "100%" }} />
            </div>
          </div>
          {/* Fakturiert bar */}
          <div
            className="thin-bar"
            title={`Fakturiert: ${viewMode === "chf" ? formatCHF(row.totalBilledCHF) : row.totalBilledHours.toFixed(1) + "h"}${viewMode === "chf" ? `\nEigenanteil: ${formatCHF(row.eigenanteilBilled)}` : ""}`}
          >
            <div className="bar-inner" style={{ width: `${billedPercentage}%` }}>
              {viewMode === "chf" && !isNegativeBilled && row.eigenanteilRatio < 1 ? (
                <>
                  <div
                    className="bar-segment eigenanteil"
                    style={{ width: `${eigenanteilPercentage > 0 ? (row.eigenanteilRatio * 100) : 0}%` }}
                  />
                  <div
                    className="bar-segment dritte"
                    style={{ width: `${eigenanteilPercentage > 0 ? ((1 - row.eigenanteilRatio) * 100) : 0}%` }}
                  />
                </>
              ) : (
                <div
                  className={`bar-segment ${isNegativeBilled ? "negative" : "billed"}`}
                  style={{ width: "100%" }}
                />
              )}
            </div>
          </div>
        </div>

        {/* Date columns */}
        <div className="col-date">
          {row.lastInvoicedDate ? formatDateCH(row.lastInvoicedDate) : "-"}
        </div>
        <div className="col-date">
          {row.nextIncomeDate ? formatDateCH(row.nextIncomeDate) : "-"}
        </div>

        {/* Amount column - Eigenanteil primary */}
        <div className="col-amount">
          {row.nextIncomeAmount > 0 ? (
            <>
              <div className={`amount-primary ${row.isOverBudget ? "over-budget" : ""}`}>
                {formatCHF(row.nextIncomeNet)}
              </div>
              {row.eigenanteilRatio < 1 && (
                <div className="amount-secondary">
                  {formatCHF(row.nextIncomeAmount)}
                </div>
              )}
            </>
          ) : (
            <div className="amount-primary">-</div>
          )}
        </div>

        {/* Actions */}
        <div className="col-actions">
          <button
            className="btn btn-sm btn-secondary"
            onClick={(e) => {
              e.stopPropagation();
              handleStundenauszugClick(row);
            }}
            title="Stundenauszug öffnen"
          >
            Stundenauszug
          </button>
          <button
            className="btn btn-sm btn-secondary"
            onClick={(e) => {
              e.stopPropagation();
              handleZahlungsplanClick(row);
            }}
            title="Zahlungsplan öffnen"
          >
            Zahlungsplan
          </button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="projects-finance-dashboard">
        <div className="dashboard-loading">Lade Projektdaten...</div>
      </div>
    );
  }

  return (
    <div className="projects-finance-dashboard">
      {/* Clean header matching Budget page style */}
      <div className="dashboard-header">
        <h2>Projekte</h2>
        <div className="view-toggle">
          <button
            className={`toggle-tab ${viewMode === "chf" ? "active" : ""}`}
            onClick={() => setViewMode("chf")}
          >
            CHF
          </button>
          <button
            className={`toggle-tab ${viewMode === "hours" ? "active" : ""}`}
            onClick={() => setViewMode("hours")}
          >
            Stunden
          </button>
        </div>
      </div>

      {projectGroups.length === 0 ? (
        <div className="dashboard-empty">Keine aktiven Projekte gefunden.</div>
      ) : (
        <div className="projects-list">
          {/* Light column labels */}
          <div className="column-labels">
            <div className="col-project">Projekt</div>
            <div className="col-bars"></div>
            <div className="col-date">Letzte Rechnung</div>
            <div className="col-date">Nächste Einnahme</div>
            <div className="col-amount">Eigenanteil</div>
            <div className="col-actions"></div>
          </div>

          {projectGroups.map((group) => (
            <div key={group.project.rowKey} className="project-group">
              {renderRow(group.project)}
              {group.phaseRows.map((phaseRow) => renderRow(phaseRow))}
            </div>
          ))}
        </div>
      )}

      {zahlungsplanProject && (
        <ZahlungsplanModal
          project={zahlungsplanProject.project}
          projectCode={zahlungsplanProject.project.code}
          phases={zahlungsplanProject.phases}
          allBudgetEntries={zahlungsplanProject.budgetEntries}
          onClose={handleZahlungsplanClose}
          onBudgetChange={handleBudgetChange}
        />
      )}
    </div>
  );
};

export default ProjectsFinanceDashboard;
